/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @file fetchAudioOptionsConfig.js - Audio provider helpers and aiadmin fetch via TenantProviderServiceV2.
 */

import Framework from '@greenville/framework';
import * as serviceConstants from '../constants/ServiceConstants';
import TokenService from '../services/TokenService';

export const normalizeTenantProviderListResponse = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  return [];
};

export const isActiveAudioProviderEntry = (entry) => entry?.active !== false && entry?.audioSupported !== false;

/**
 * @param {Array} config
 * @returns {Array<{ providerId: string, providerName: string }>}
 */
export const getUniqueProviders = (config) => {
  const seen = new Map();
  (config || []).filter(isActiveAudioProviderEntry).forEach((entry) => {
    if (!seen.has(entry.providerId)) {
      seen.set(entry.providerId, {
        providerId: entry.providerId,
        providerName: entry.providerName
      });
    }
  });
  return Array.from(seen.values());
};

/**
 * @param {Array} config
 * @param {string} providerId
 * @returns {Array<{ model: string }>}
 */
export const getModelsForProvider = (config, providerId) => {
  if (!providerId) {
    return [];
  }
  const models = new Set();
  (config || [])
    .filter((entry) => isActiveAudioProviderEntry(entry) && entry.providerId === providerId)
    .forEach((entry) => models.add(entry.model));
  return Array.from(models).map((model) => ({ model }));
};

/**
 * @param {Array} config
 * @param {string} providerId
 * @param {string} model
 * @returns {Array<{ name: string, type: string, language: string, isDefault?: boolean }>}
 */
export const getVoicesForProviderModel = (config, providerId, model) => {
  if (!providerId || !model) {
    return [];
  }
  const entry = (config || []).find(
    (item) => isActiveAudioProviderEntry(item) && item.providerId === providerId && item.model === model
  );
  return entry?.voices || [];
};

export const findVoiceInConfig = (config, providerId, model, voiceName) => {
  if (!voiceName) {
    return null;
  }
  return getVoicesForProviderModel(config, providerId, model).find((voice) => voice.name === voiceName) || null;
};

/**
 * GET aiadmin /tenants/{tenantId}/providers?audio=true via TenantProviderServiceV2
 *
 * @param {string} tenantId
 * @returns {Promise<Array>}
 */
export default async function fetchTenantAudioProviders(tenantId) {
  if (!tenantId) {
    throw new Error('Tenant ID is required to load audio providers');
  }

  TokenService.requestCounter += 1;
  const requestId = `audio_providers_${Date.now()}_${TokenService.requestCounter}`;

  const dataPromise = TokenService.awaitEventResult({
    requestId,
    successEvent: serviceConstants.GET_TENANT_AUDIO_PROVIDERS_SUCCESS,
    errorEvent: serviceConstants.GET_TENANT_AUDIO_PROVIDERS_ERROR
  });

  Framework.getEventManager().publish(serviceConstants.GET_TENANT_PROVIDER_LIST, {
    tenantId,
    audio: true,
    requestId
  });

  const data = await dataPromise;
  return normalizeTenantProviderListResponse(data);
}
